// BILLING-PLANS (§6.2): Operator › Billing › Plans — the platform plan and maintenance tier
// versions (a new version is a modal; a version with companies or DEs on it has no remove
// icon), the charge catalog (Add, Edit, Remove on hover), the defaults (one modal) and each
// template's tier and setup fee. A platform admin edits (`can_edit`); everyone else reads.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  InfoTip,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CopyPlusIcon, KeyRoundIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../../api/client";
import { ONE_TIME_CHARGES } from "../../billing/flags";
import { centsToDollarsText, parseDollarsToCents } from "../../billing/money";
import { SectionHead, TIER_LABEL } from "../../billing/plan/parts";
import {
  type BillingDefaults,
  type ChargeCatalogItem,
  type MaintenancePlanRow,
  type MaintenanceTier,
  opBillingKeys,
  type PaymentMethod,
  type PlatformPlanRow,
  priceListsQuery,
  type TemplateBillingRow,
} from "../../billing/plan-queries";
import { formatDate, formatMoney } from "../../format";
import { useOperatorName } from "../../operator-name";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import {
  FormDialog,
  MoneyField,
  optionalCents,
  SelectField,
  TextField,
  todayIso,
} from "./form-parts";

const money = (c: number) => formatMoney(c, "USD");
const TIERS: { value: MaintenanceTier; label: string }[] = [
  { value: "standard", label: "Standard" },
  { value: "advanced", label: "Advanced" },
  { value: "managed", label: "Managed" },
];
export const METHOD_LABEL: Record<PaymentMethod, string> = {
  card: "Card",
  us_bank_account: "ACH debit",
  customer_balance: "Bank transfer",
};
const KIND_LABEL: Record<ChargeCatalogItem["kind"], string> = {
  setup: "Setup",
  build_milestone: "Build milestone",
  maintenance: "Maintenance",
  custom: "Custom",
};

function VersionBadge({ current, active }: { current: boolean; active: boolean }) {
  if (!active) return <Badge variant="neutral">Retired</Badge>;
  return current ? (
    <Badge variant="success">Current</Badge>
  ) : (
    <Badge variant="info">Scheduled</Badge>
  );
}

export function PlansTab({ api }: { api: Api }) {
  const q = useQuery(priceListsQuery(api));
  if (q.isPending) return <Loading label="Loading price lists" rows={6} />;
  if (q.isError) return <LoadError what="Price lists" error={q.error} />;
  const lists = q.data;
  const edit = lists.can_edit;
  return (
    <div className="flex flex-col gap-8" data-slot="billing-plans">
      {edit ? null : (
        <p className="flex items-center gap-1 text-[13px] text-muted-foreground">
          Read-only
          <InfoTip label="About editing price lists">
            A platform admin edits the price lists.
          </InfoTip>
        </p>
      )}
      <PlatformPlans api={api} rows={lists.platform} edit={edit} />
      {edit ? <ModelMap api={api} /> : null}
      <MaintenanceTiers api={api} rows={lists.maintenance} edit={edit} />
      {ONE_TIME_CHARGES ? <Catalog api={api} rows={lists.catalog} edit={edit} /> : null}
      <Defaults api={api} defaults={lists.defaults} edit={edit} />
      <Templates api={api} rows={lists.templates} edit={edit} />
    </div>
  );
}

function useInvalidatePrices() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: opBillingKeys.all() });
}

function RetireDialog({
  what,
  open,
  onClose,
  onConfirm,
}: {
  what: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Retire {what}?</AlertDialogTitle>
          <AlertDialogDescription>New companies can no longer get it.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onConfirm}>
            Retire
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Head({ children, right }: { children: ReactNode; right?: boolean }) {
  return (
    <TableHead scope="col" className={right ? "text-right" : undefined}>
      {children}
    </TableHead>
  );
}

// Platform plans --------------------------------------------------------------------------------

function PlatformPlans({ api, rows, edit }: { api: Api; rows: PlatformPlanRow[]; edit: boolean }) {
  const refresh = useInvalidatePrices();
  const [from, setFrom] = useState<PlatformPlanRow | "new" | null>(null);
  const [retiring, setRetiring] = useState<PlatformPlanRow | null>(null);
  const retire = useMutation({
    mutationFn: (p: PlatformPlanRow) =>
      api.client.call("operator.billing.plans.retire", { params: { plan_id: p.id } }),
    onSuccess: () => setRetiring(null),
    onSettled: refresh,
  });
  // MODEL-KEYS-PLANS: per plan (its key, every version), may its companies bring their own keys.
  const byo = useMutation({
    mutationFn: (v: { key: string; on: boolean }) =>
      api.client.call("operator.billing.planModelAccess.put", {
        params: { plan_key: v.key },
        body: { byo_model_keys: v.on },
      }),
    onSuccess: (r) =>
      toast.add({
        title: `${r.plan_key}: own model keys ${r.byo_model_keys ? "allowed" : "not allowed"}`,
        type: "success",
      }),
    onError: () => toast.add({ title: "The setting was not saved.", type: "error" }),
    onSettled: refresh,
  });
  return (
    <section aria-labelledby="op-plans" className="flex flex-col gap-2">
      <SectionHead
        id="op-plans"
        title="Platform plans"
        info="One per company. A new version applies to new companies now and to existing ones at their next renewal. Own keys: companies on the plan may use their own AI provider keys and pay the provider; we bill them no tokens (every version of the plan)."
      >
        {edit ? (
          <Button size="sm" variant="secondary" onClick={() => setFrom("new")}>
            <PlusIcon data-icon="inline-start" />
            New version
          </Button>
        ) : null}
      </SectionHead>
      <Table
        className={`${DENSE_TABLE} [&_td]:px-2.5 [&_th]:px-2.5 [&_thead_th]:h-auto [&_thead_th]:py-1.5 [&_thead_th]:whitespace-normal`}
        data-slot="op-platform-plans"
      >
        <TableCaption className="sr-only">Platform plan versions.</TableCaption>
        <TableHeader>
          <TableRow>
            <Head>Key</Head>
            <Head>Name</Head>
            <Head right>Price</Head>
            <Head right>People included</Head>
            <Head right>Per extra person</Head>
            <Head>Version</Head>
            <Head>Effective from</Head>
            <Head right>Companies</Head>
            <Head>
              <span className="sr-only">Actions</span>
            </Head>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} className="group/row" data-plan={`${p.key}-v${p.version}`}>
              <TableHead scope="row" className="font-medium">
                <span className="inline-flex items-center gap-1.5">
                  {p.key}
                  {p.byo_model_keys ? (
                    <Badge variant="info" data-slot="plan-byo-keys">
                      Own keys
                    </Badge>
                  ) : null}
                </span>
              </TableHead>
              <TableCell>{p.name}</TableCell>
              <TableCell className="text-right tabular-nums">
                {money(p.base_price_minor)} / {p.interval}
              </TableCell>
              <TableCell className="text-right tabular-nums">{p.included_seats}</TableCell>
              <TableCell className="text-right tabular-nums">
                {money(p.extra_seat_price_minor)}
              </TableCell>
              <TableCell>
                <span className="inline-flex items-center gap-1.5">
                  v{p.version}
                  <VersionBadge current={p.current} active={p.active} />
                </span>
              </TableCell>
              <TableCell>{formatDate(p.effective_from)}</TableCell>
              <TableCell className="text-right tabular-nums">{p.companies}</TableCell>
              <TableCell className="w-px">
                {edit ? (
                  <RowActions>
                    <RowAction
                      icon={KeyRoundIcon}
                      label={
                        p.byo_model_keys
                          ? `Stop own model keys on ${p.key}`
                          : `Allow own model keys on ${p.key}`
                      }
                      tip={p.byo_model_keys ? "Stop own keys" : "Allow own keys"}
                      disabled={byo.isPending}
                      onClick={() => byo.mutate({ key: p.key, on: !p.byo_model_keys })}
                    />
                    <RowAction
                      icon={CopyPlusIcon}
                      label={`New version of ${p.key}`}
                      tip="New version"
                      onClick={() => setFrom(p)}
                    />
                    {p.companies === 0 && p.active ? (
                      <RowAction
                        icon={Trash2Icon}
                        label={`Retire ${p.key} v${p.version}`}
                        tip="Retire"
                        className="hover:text-danger-text"
                        onClick={() => setRetiring(p)}
                      />
                    ) : null}
                  </RowActions>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {from ? (
        <PlanVersionDialog
          api={api}
          from={from === "new" ? (rows[0] ?? null) : from}
          onClose={() => setFrom(null)}
        />
      ) : null}
      <RetireDialog
        what={retiring ? `${retiring.key} v${retiring.version}` : ""}
        open={retiring !== null}
        onClose={() => setRetiring(null)}
        onConfirm={() => retiring && retire.mutate(retiring)}
      />
    </section>
  );
}

function PlanVersionDialog({
  api,
  from,
  onClose,
}: {
  api: Api;
  from: PlatformPlanRow | null;
  onClose: () => void;
}) {
  const refresh = useInvalidatePrices();
  const [f, setF] = useState({
    key: from?.key ?? "base",
    name: from?.name ?? "Platform",
    price: from ? centsToDollarsText(from.base_price_minor) : "",
    included: String(from?.included_seats ?? 5),
    extra: from ? centsToDollarsText(from.extra_seat_price_minor) : "",
    grace: String(from?.grace_days ?? 14),
    effective: todayIso(),
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const price = parseDollarsToCents(f.price);
  const extra = parseDollarsToCents(f.extra);
  const valid =
    /^[a-z0-9][a-z0-9-]{1,62}$/.test(f.key) &&
    f.name.trim() !== "" &&
    price !== null &&
    extra !== null &&
    /^\d+$/.test(f.included) &&
    /^\d+$/.test(f.grace) &&
    Number(f.grace) <= 90 &&
    f.effective >= todayIso();
  const save = useMutation({
    mutationFn: () =>
      api.client.call("operator.billing.plans.create", {
        idempotencyKey: newIdempotencyKey(),
        body: {
          key: f.key,
          name: f.name.trim(),
          interval: "month",
          base_price_minor: price ?? 0,
          included_seats: Number(f.included),
          extra_seat_price_minor: extra ?? 0,
          grace_days: Number(f.grace),
          effective_from: f.effective,
        },
      }),
    onSuccess: (p) => {
      toast.add({ title: `${p.key} v${p.version} saved`, type: "success" });
      onClose();
    },
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="New platform plan version"
      pending={save.isPending}
      canSubmit={valid}
      submitLabel="Save version"
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="Key" value={f.key} onChange={(key) => set({ key })} required />
        <TextField label="Name" value={f.name} onChange={(name) => set({ name })} required />
        <MoneyField
          label="Price per month ($)"
          value={f.price}
          onChange={(price) => set({ price })}
        />
        <TextField
          label="People included"
          inputMode="numeric"
          value={f.included}
          onChange={(included) => set({ included })}
          required
        />
        <MoneyField
          label="Per extra person ($)"
          value={f.extra}
          onChange={(extra) => set({ extra })}
        />
        <TextField
          label="Grace days"
          inputMode="numeric"
          value={f.grace}
          onChange={(grace) => set({ grace })}
          info="Days after a failed payment or a missed due date before the company is suspended."
          required
        />
        <TextField
          label="Effective from"
          type="date"
          min={todayIso()}
          value={f.effective}
          onChange={(effective) => set({ effective })}
          required
        />
      </div>
    </FormDialog>
  );
}

// Model map (MODEL-KEYS-PLANS) ------------------------------------------------------------------

type MapEntry = { alias: string; target: string };
const TARGET_RE = /^[a-z0-9][a-z0-9_-]*\/[^/\s]\S*$/;

/**
 * The gateway alias → provider/model map a company's own keys are routed by (platform admins).
 * An alias in use with no mapping shows as "Unmapped model": it is never guessed, and a DE on own
 * keys that runs it is not handed off until it is mapped.
 */
function ModelMap({ api }: { api: Api }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [...opBillingKeys.all(), "model-provider-map"],
    queryFn: () => api.client.call("operator.billing.modelProviderMap.get", {}),
  });
  const [editing, setEditing] = useState<MapEntry | null>(null);
  const [isNew, setIsNew] = useState(false);
  const save = useMutation({
    mutationFn: (entries: MapEntry[]) =>
      api.client.call("operator.billing.modelProviderMap.put", { body: { entries } }),
    onSuccess: () => {
      setEditing(null);
      toast.add({ title: "Model map saved", type: "success" });
    },
    onError: () => toast.add({ title: "The model map was not saved.", type: "error" }),
    onSettled: () => qc.invalidateQueries({ queryKey: opBillingKeys.all() }),
  });
  if (q.isPending) return <Loading label="Loading the model map" rows={3} />;
  if (q.isError) return <LoadError what="Model map" error={q.error} />;
  const entries: MapEntry[] = q.data.entries.map(({ alias, target }) => ({ alias, target }));
  const put = (next: MapEntry[]) => save.mutate(next);
  const open = (e: MapEntry, fresh: boolean) => {
    setIsNew(fresh);
    setEditing(e);
  };
  const valid =
    editing !== null &&
    editing.alias.trim() !== "" &&
    TARGET_RE.test(editing.target.trim()) &&
    (!isNew || !entries.some((x) => x.alias === editing.alias.trim()));
  return (
    <section aria-labelledby="op-model-map" className="flex flex-col gap-2">
      <SectionHead
        id="op-model-map"
        title="Model map"
        info="Which provider and model each gateway alias runs on a company's own keys. An unmapped model is never guessed."
      >
        <Button size="sm" variant="secondary" onClick={() => open({ alias: "", target: "" }, true)}>
          <PlusIcon data-icon="inline-start" />
          Add
        </Button>
      </SectionHead>
      <Table className={DENSE_TABLE} data-slot="op-model-map">
        <TableCaption className="sr-only">Model alias to provider map.</TableCaption>
        <TableHeader>
          <TableRow>
            <Head>Alias</Head>
            <Head>Provider / model</Head>
            <Head>
              <span className="sr-only">Actions</span>
            </Head>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.data.unmapped.map((alias) => (
            <TableRow key={`u-${alias}`} className="group/row" data-unmapped={alias}>
              <TableHead scope="row" className="font-medium">
                {alias}
              </TableHead>
              <TableCell>
                <Badge variant="warning">Unmapped model</Badge>
              </TableCell>
              <TableCell className="w-px">
                <RowActions>
                  <RowAction
                    icon={PlusIcon}
                    label={`Map ${alias}`}
                    tip="Map"
                    onClick={() => open({ alias, target: "" }, true)}
                  />
                </RowActions>
              </TableCell>
            </TableRow>
          ))}
          {entries.map((e) => (
            <TableRow key={e.alias} className="group/row" data-alias={e.alias}>
              <TableHead scope="row" className="font-medium">
                {e.alias}
              </TableHead>
              <TableCell className="font-mono text-[12px]">{e.target}</TableCell>
              <TableCell className="w-px">
                <RowActions>
                  <RowAction
                    icon={PencilIcon}
                    label={`Edit ${e.alias}`}
                    tip="Edit"
                    onClick={() => open(e, false)}
                  />
                  <RowAction
                    icon={Trash2Icon}
                    label={`Remove ${e.alias}`}
                    tip="Remove"
                    className="hover:text-danger-text"
                    onClick={() => put(entries.filter((x) => x.alias !== e.alias))}
                  />
                </RowActions>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {editing ? (
        <FormDialog
          open
          onOpenChange={(o) => (o ? null : setEditing(null))}
          title={isNew ? "Map a model" : `Edit ${editing.alias}`}
          pending={save.isPending}
          canSubmit={valid}
          submitLabel="Save"
          onSubmit={() => {
            const next = { alias: editing.alias.trim(), target: editing.target.trim() };
            put([...entries.filter((x) => x.alias !== next.alias), next]);
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Alias"
              value={editing.alias}
              onChange={(alias) => setEditing({ ...editing, alias })}
              disabled={!isNew}
              required
            />
            <TextField
              label="Provider / model"
              value={editing.target}
              onChange={(target) => setEditing({ ...editing, target })}
              info="provider/model id, e.g. anthropic/claude-haiku-5-5"
              required
            />
          </div>
        </FormDialog>
      ) : null}
    </section>
  );
}

// Maintenance tiers -----------------------------------------------------------------------------

function MaintenanceTiers({
  api,
  rows,
  edit,
}: {
  api: Api;
  rows: MaintenancePlanRow[];
  edit: boolean;
}) {
  const refresh = useInvalidatePrices();
  const operator = useOperatorName();
  const [from, setFrom] = useState<MaintenancePlanRow | "new" | null>(null);
  const [retiring, setRetiring] = useState<MaintenancePlanRow | null>(null);
  const retire = useMutation({
    mutationFn: (m: MaintenancePlanRow) =>
      api.client.call("operator.billing.maintenancePlans.retire", { params: { plan_id: m.id } }),
    onSuccess: () => setRetiring(null),
    onSettled: refresh,
  });
  return (
    <section aria-labelledby="op-tiers" className="flex flex-col gap-2">
      <SectionHead
        id="op-tiers"
        title="Maintenance tiers"
        info="A price per DE per month; its AGs are included up to the cap."
      >
        {edit ? (
          <Button size="sm" variant="secondary" onClick={() => setFrom("new")}>
            <PlusIcon data-icon="inline-start" />
            New version
          </Button>
        ) : null}
      </SectionHead>
      <Table className={DENSE_TABLE} data-slot="op-maintenance-tiers">
        <TableCaption className="sr-only">Maintenance tier versions.</TableCaption>
        <TableHeader>
          <TableRow>
            <Head>Tier</Head>
            <Head>Name</Head>
            <Head right>Price</Head>
            <Head right>AG cap</Head>
            <Head>Supervision</Head>
            <Head>Version</Head>
            <Head>Effective from</Head>
            <Head right>DEs</Head>
            <Head>
              <span className="sr-only">Actions</span>
            </Head>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((m) => (
            <TableRow key={m.id} className="group/row" data-tier={`${m.tier}-v${m.version}`}>
              <TableHead scope="row" className="font-medium">
                {TIER_LABEL[m.tier]}
              </TableHead>
              <TableCell>{m.name}</TableCell>
              <TableCell className="text-right tabular-nums">{money(m.price_minor)}</TableCell>
              <TableCell className="text-right tabular-nums">{m.ag_cap}</TableCell>
              <TableCell>{m.supervision === "agency" ? operator : "Client"}</TableCell>
              <TableCell>
                <span className="inline-flex items-center gap-1.5">
                  v{m.version}
                  <VersionBadge current={m.current} active={m.active} />
                </span>
              </TableCell>
              <TableCell>{formatDate(m.effective_from)}</TableCell>
              <TableCell className="text-right tabular-nums">{m.des}</TableCell>
              <TableCell className="w-px">
                {edit ? (
                  <RowActions>
                    <RowAction
                      icon={CopyPlusIcon}
                      label={`New version of ${TIER_LABEL[m.tier]}`}
                      tip="New version"
                      onClick={() => setFrom(m)}
                    />
                    {m.des === 0 && m.active ? (
                      <RowAction
                        icon={Trash2Icon}
                        label={`Retire ${TIER_LABEL[m.tier]} v${m.version}`}
                        tip="Retire"
                        className="hover:text-danger-text"
                        onClick={() => setRetiring(m)}
                      />
                    ) : null}
                  </RowActions>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {from ? (
        <TierVersionDialog
          api={api}
          from={from === "new" ? (rows[0] ?? null) : from}
          onClose={() => setFrom(null)}
        />
      ) : null}
      <RetireDialog
        what={retiring ? `${TIER_LABEL[retiring.tier]} v${retiring.version}` : ""}
        open={retiring !== null}
        onClose={() => setRetiring(null)}
        onConfirm={() => retiring && retire.mutate(retiring)}
      />
    </section>
  );
}

function TierVersionDialog({
  api,
  from,
  onClose,
}: {
  api: Api;
  from: MaintenancePlanRow | null;
  onClose: () => void;
}) {
  const refresh = useInvalidatePrices();
  const operator = useOperatorName();
  const [f, setF] = useState({
    tier: (from?.tier ?? "standard") as MaintenanceTier,
    name: from?.name ?? "Standard",
    price: from ? centsToDollarsText(from.price_minor) : "",
    cap: String(from?.ag_cap ?? 5),
    supervision: (from?.supervision ?? "client") as MaintenancePlanRow["supervision"],
    effective: todayIso(),
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const price = parseDollarsToCents(f.price);
  const valid =
    f.name.trim() !== "" &&
    price !== null &&
    /^\d+$/.test(f.cap) &&
    Number(f.cap) <= 1000 &&
    f.effective >= todayIso();
  const save = useMutation({
    mutationFn: () =>
      api.client.call("operator.billing.maintenancePlans.create", {
        idempotencyKey: newIdempotencyKey(),
        body: {
          tier: f.tier,
          name: f.name.trim(),
          price_minor: price ?? 0,
          ag_cap: Number(f.cap),
          supervision: f.supervision,
          effective_from: f.effective,
        },
      }),
    onSuccess: (m) => {
      toast.add({ title: `${TIER_LABEL[m.tier]} v${m.version} saved`, type: "success" });
      onClose();
    },
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="New maintenance tier version"
      pending={save.isPending}
      canSubmit={valid}
      submitLabel="Save version"
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Tier"
          value={f.tier}
          options={TIERS}
          onChange={(tier) => set({ tier, name: TIER_LABEL[tier] })}
        />
        <TextField label="Name" value={f.name} onChange={(name) => set({ name })} required />
        <MoneyField
          label="Price per DE per month ($)"
          value={f.price}
          onChange={(price) => set({ price })}
        />
        <TextField
          label="AG cap"
          inputMode="numeric"
          value={f.cap}
          onChange={(cap) => set({ cap })}
          required
        />
        <SelectField
          label="Supervision"
          value={f.supervision}
          options={[
            { value: "client", label: "Client" },
            { value: "agency", label: operator },
          ]}
          onChange={(supervision) => set({ supervision })}
          info={`${operator}: an agency user holds the supervisor seat.`}
        />
        <TextField
          label="Effective from"
          type="date"
          min={todayIso()}
          value={f.effective}
          onChange={(effective) => set({ effective })}
          required
        />
      </div>
    </FormDialog>
  );
}

// Charge catalog ---------------------------------------------------------------------------------

function Catalog({ api, rows, edit }: { api: Api; rows: ChargeCatalogItem[]; edit: boolean }) {
  const refresh = useInvalidatePrices();
  const [editing, setEditing] = useState<ChargeCatalogItem | "new" | null>(null);
  const [removing, setRemoving] = useState<ChargeCatalogItem | null>(null);
  const remove = useMutation({
    mutationFn: (c: ChargeCatalogItem) =>
      api.client.call("operator.billing.catalog.delete", { params: { item_id: c.id } }),
    onSuccess: () => setRemoving(null),
    onSettled: refresh,
  });
  return (
    <section aria-labelledby="op-catalog" className="flex flex-col gap-2">
      <SectionHead
        id="op-catalog"
        title="Charge catalog"
        info="One-off lines the invoice and quote modal picks from; a custom line is always possible."
      >
        {edit ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing("new")}>
            <PlusIcon data-icon="inline-start" />
            Add
          </Button>
        ) : null}
      </SectionHead>
      {rows.length === 0 ? (
        <EmptyState variant="compact" align="start" title="No catalog lines" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="op-catalog">
          <TableCaption className="sr-only">The charge catalog.</TableCaption>
          <TableHeader>
            <TableRow>
              <Head>Key</Head>
              <Head>Name</Head>
              <Head>Kind</Head>
              <Head right>Default price</Head>
              <Head>Taxable</Head>
              <Head>
                <span className="sr-only">Actions</span>
              </Head>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} className="group/row" data-catalog={c.key}>
                <TableHead scope="row" className="font-medium">
                  {c.key}
                </TableHead>
                <TableCell>{c.name}</TableCell>
                <TableCell>{KIND_LABEL[c.kind]}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(c.default_price_minor)}
                </TableCell>
                <TableCell>{c.taxable ? "Yes" : "No"}</TableCell>
                <TableCell className="w-px">
                  {edit ? (
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${c.name}`}
                        tip="Edit"
                        onClick={() => setEditing(c)}
                      />
                      <RowAction
                        icon={Trash2Icon}
                        label={`Remove ${c.name}`}
                        tip="Remove"
                        className="hover:text-danger-text"
                        onClick={() => setRemoving(c)}
                      />
                    </RowActions>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing ? (
        <CatalogDialog
          api={api}
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <AlertDialog open={removing !== null} onOpenChange={(o) => (o ? null : setRemoving(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{removing?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Quotes and invoices that used it keep their copies.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => removing && remove.mutate(removing)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function CatalogDialog({
  api,
  item,
  onClose,
}: {
  api: Api;
  item: ChargeCatalogItem | null;
  onClose: () => void;
}) {
  const refresh = useInvalidatePrices();
  const [f, setF] = useState({
    key: item?.key ?? "",
    name: item?.name ?? "",
    kind: (item?.kind ?? "custom") as ChargeCatalogItem["kind"],
    price: item ? centsToDollarsText(item.default_price_minor) : "",
    taxable: item?.taxable ?? false,
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const price = parseDollarsToCents(f.price);
  const valid =
    (item !== null || /^[a-z0-9][a-z0-9_-]{0,63}$/.test(f.key)) &&
    f.name.trim() !== "" &&
    price !== null;
  const save = useMutation({
    mutationFn: () =>
      item
        ? api.client.call("operator.billing.catalog.update", {
            params: { item_id: item.id },
            body: {
              name: f.name.trim(),
              kind: f.kind,
              default_price_minor: price ?? 0,
              taxable: f.taxable,
            },
          })
        : api.client.call("operator.billing.catalog.create", {
            idempotencyKey: newIdempotencyKey(),
            body: {
              key: f.key,
              name: f.name.trim(),
              kind: f.kind,
              default_price_minor: price ?? 0,
              taxable: f.taxable,
            },
          }),
    onSuccess: onClose,
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={item ? `Edit ${item.name}` : "Add a catalog line"}
      pending={save.isPending}
      canSubmit={valid}
      submitLabel={item ? "Save" : "Add"}
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Key"
          value={f.key}
          disabled={item !== null}
          onChange={(key) => set({ key })}
          required
        />
        <TextField label="Name" value={f.name} onChange={(name) => set({ name })} required />
        <SelectField
          label="Kind"
          value={f.kind}
          options={Object.entries(KIND_LABEL).map(([value, label]) => ({
            value: value as ChargeCatalogItem["kind"],
            label,
          }))}
          onChange={(kind) => set({ kind })}
        />
        <MoneyField label="Default price ($)" value={f.price} onChange={(p) => set({ price: p })} />
      </div>
      <span className="flex items-center gap-2 text-[13px]">
        <Checkbox
          checked={f.taxable}
          onCheckedChange={(c) => set({ taxable: c === true })}
          aria-label="Taxable"
        />
        Taxable
      </span>
    </FormDialog>
  );
}

// Defaults ---------------------------------------------------------------------------------------

function Defaults({ api, defaults, edit }: { api: Api; defaults: BillingDefaults; edit: boolean }) {
  const [open, setOpen] = useState(false);
  const facts: [string, string][] = [
    ["Net terms", `${defaults.net_terms_days} days`],
    ...(ONE_TIME_CHARGES
      ? ([["Template setup fee", money(defaults.setup_fee_minor)]] as [string, string][])
      : []),
    ["Reminder day", `Day ${defaults.reminder_day}`],
    ["Payment methods", defaults.payment_methods.map((m) => METHOD_LABEL[m]).join(", ")],
  ];
  return (
    <section aria-labelledby="op-defaults" className="flex flex-col gap-2">
      <SectionHead
        id="op-defaults"
        title="Defaults"
        info="What a company gets unless it has its own."
      >
        {edit ? (
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            <PencilIcon data-icon="inline-start" />
            Edit
          </Button>
        ) : null}
      </SectionHead>
      <dl
        className="grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] sm:grid-cols-4"
        data-slot="op-defaults"
      >
        {facts.map(([k, v]) => (
          <div key={k}>
            <dt className="text-muted-foreground">{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {open ? (
        <DefaultsDialog api={api} defaults={defaults} onClose={() => setOpen(false)} />
      ) : null}
    </section>
  );
}

function DefaultsDialog({
  api,
  defaults,
  onClose,
}: {
  api: Api;
  defaults: BillingDefaults;
  onClose: () => void;
}) {
  const refresh = useInvalidatePrices();
  const [f, setF] = useState({
    net: String(defaults.net_terms_days),
    fee: centsToDollarsText(defaults.setup_fee_minor),
    reminder: String(defaults.reminder_day),
    methods: defaults.payment_methods,
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const fee = parseDollarsToCents(f.fee);
  const valid =
    /^\d+$/.test(f.net) &&
    Number(f.net) <= 120 &&
    fee !== null &&
    /^\d+$/.test(f.reminder) &&
    Number(f.reminder) >= 1 &&
    Number(f.reminder) <= 60 &&
    f.methods.length > 0;
  const save = useMutation({
    mutationFn: () =>
      api.client.call("operator.billing.defaults.put", {
        body: {
          net_terms_days: Number(f.net),
          setup_fee_minor: fee ?? 0,
          reminder_day: Number(f.reminder),
          payment_methods: f.methods,
        },
      }),
    onSuccess: onClose,
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="Billing defaults"
      pending={save.isPending}
      canSubmit={valid}
      submitLabel="Save"
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          label="Net terms (days)"
          inputMode="numeric"
          value={f.net}
          onChange={(net) => set({ net })}
          required
        />
        {ONE_TIME_CHARGES ? (
          <MoneyField
            label="Template setup fee ($)"
            value={f.fee}
            onChange={(fee) => set({ fee })}
          />
        ) : null}
        <TextField
          label="Reminder day"
          inputMode="numeric"
          value={f.reminder}
          onChange={(reminder) => set({ reminder })}
          info="Days after a failure or due date when the reminder goes out."
          required
        />
      </div>
      <PaymentMethodsField value={f.methods} onChange={(methods) => set({ methods })} />
    </FormDialog>
  );
}

export function PaymentMethodsField({
  value,
  onChange,
}: {
  value: PaymentMethod[];
  onChange: (v: PaymentMethod[]) => void;
}) {
  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="mb-2 flex items-center gap-1 text-sm font-medium">
        Payment methods
        <InfoTip label="About payment methods">
          What Stripe's hosted invoice page offers. Bank transfer gives the client a virtual account
          number.
        </InfoTip>
      </legend>
      {(Object.keys(METHOD_LABEL) as PaymentMethod[]).map((m) => (
        <span key={m} className="flex items-center gap-2 text-[13px]">
          <Checkbox
            checked={value.includes(m)}
            aria-label={METHOD_LABEL[m]}
            onCheckedChange={(c) =>
              onChange(c === true ? [...value, m] : value.filter((x) => x !== m))
            }
          />
          {METHOD_LABEL[m]}
        </span>
      ))}
    </fieldset>
  );
}

// Templates --------------------------------------------------------------------------------------

function Templates({ api, rows, edit }: { api: Api; rows: TemplateBillingRow[]; edit: boolean }) {
  const [editing, setEditing] = useState<TemplateBillingRow | null>(null);
  return (
    <section aria-labelledby="op-templates" className="flex flex-col gap-2">
      <SectionHead
        id="op-templates"
        title="Templates"
        info={
          ONE_TIME_CHARGES
            ? "The tier a new DE from the template gets, and its setup fee (blank: the default)."
            : "The tier a new DE from the template gets."
        }
      />
      {rows.length === 0 ? (
        <EmptyState variant="compact" align="start" title="No templates yet" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="op-templates">
          <TableCaption className="sr-only">Each template's tier and setup fee.</TableCaption>
          <TableHeader>
            <TableRow>
              <Head>Template</Head>
              <Head>Version</Head>
              <Head>Tier</Head>
              {ONE_TIME_CHARGES ? <Head right>Setup fee</Head> : null}
              <Head>
                <span className="sr-only">Actions</span>
              </Head>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.template_id} className="group/row" data-template={t.key}>
                <TableHead scope="row" className="font-medium">
                  {t.name}
                </TableHead>
                <TableCell>v{t.version}</TableCell>
                <TableCell>{t.maintenance_tier ? TIER_LABEL[t.maintenance_tier] : "—"}</TableCell>
                {ONE_TIME_CHARGES ? (
                  <TableCell className="text-right tabular-nums">
                    {t.setup_fee_minor === null ? "Default" : money(t.setup_fee_minor)}
                  </TableCell>
                ) : null}
                <TableCell className="w-px">
                  {edit ? (
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit ${t.name}'s billing`}
                        tip="Edit"
                        onClick={() => setEditing(t)}
                      />
                    </RowActions>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editing ? <TemplateDialog api={api} row={editing} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

function TemplateDialog({
  api,
  row,
  onClose,
}: {
  api: Api;
  row: TemplateBillingRow;
  onClose: () => void;
}) {
  const refresh = useInvalidatePrices();
  const [tier, setTier] = useState<MaintenanceTier | "none">(row.maintenance_tier ?? "none");
  const [fee, setFee] = useState(
    row.setup_fee_minor === null ? "" : centsToDollarsText(row.setup_fee_minor),
  );
  const cents = optionalCents(fee);
  const save = useMutation({
    mutationFn: () =>
      api.client.call("operator.billing.templates.put", {
        params: { template_id: row.template_id },
        body: { maintenance_tier: tier === "none" ? null : tier, setup_fee_minor: cents ?? null },
      }),
    onSuccess: onClose,
    onSettled: refresh,
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={`${row.name}: billing`}
      pending={save.isPending}
      canSubmit={cents !== undefined}
      submitLabel="Save"
      onSubmit={() => save.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Tier"
          value={tier}
          options={[{ value: "none" as const, label: "None" }, ...TIERS]}
          onChange={setTier}
        />
        {ONE_TIME_CHARGES ? (
          <MoneyField
            label="Setup fee ($)"
            value={fee}
            onChange={setFee}
            optional
            info="Blank: the default setup fee. 0 means no fee."
          />
        ) : null}
      </div>
    </FormDialog>
  );
}
